import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { SITE_URL, SITE_BRAND } from "@/lib/seo-config";
import { PERSONA_TYPES } from "@/lib/persona-types";
import { ArrowRight, Lock, Camera, ShieldCheck, BadgeCheck, Handshake, Receipt, Sparkles } from "lucide-react";

function buildPersonaSEO(persona) {
  return {
    title: persona.seo.title,
    description: persona.seo.description,
    keywords: persona.seo.keywords,
    canonical: `/pour/${persona.slug}`,
    ogType: "website",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": `${SITE_BRAND} pour ${persona.label}`,
        "description": persona.seo.description,
        "provider": { "@type": "Organization", "name": SITE_BRAND, "url": SITE_URL },
        "areaServed": { "@type": "Country", "name": "France" },
        "audience": {
          "@type": persona.slug === "particulier" ? "PeopleAudience" : "BusinessAudience",
          "audienceType": persona.label,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Pour qui ?", "item": `${SITE_URL}/pour-qui` },
          { "@type": "ListItem", "position": 3, "name": persona.label, "item": `${SITE_URL}/pour/${persona.slug}` },
        ],
      },
    ],
  };
}

export default function PersonaPage() {
  const { persona: slug } = useParams();
  const p = PERSONA_TYPES[slug];

  if (!p) return <Navigate to="/pour-qui" replace />;

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid={`persona-page-${slug}`}>
      <SEO cfg={buildPersonaSEO(p)} />
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B] relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20 relative">
          <nav className="mono text-[11px] uppercase tracking-widest text-zinc-400 mb-5" aria-label="Fil d'ariane">
            <Link to="/" className="hover:text-[#FF4D00]">Accueil</Link>
            <span className="mx-2">/</span>
            <Link to="/pour-qui" className="hover:text-[#FF4D00]">Pour qui ?</Link>
            <span className="mx-2">/</span>
            <span className="text-[#FF4D00]">{p.label}</span>
          </nav>
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// {p.hero.overline}</div>
          <h1 className="display text-4xl lg:text-6xl tracking-tighter mt-3 max-w-4xl leading-[0.95]">{p.hero.h1}</h1>
          <p className="mt-6 text-zinc-300 text-base lg:text-lg max-w-3xl">{p.hero.lead}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={p.ctaLink}>
              <button className="btn-batisur flex items-center gap-2" data-testid={`persona-cta-${slug}`}>
                {p.ctaTitle} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits (6 H3) */}
      <section className="bg-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-6xl mx-auto px-6 py-16 lg:py-20">
          <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Ce que Batisur apporte concrètement</div>
          <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 leading-[0.95]">
            Fonctionnalités dédiées <span className="text-[#FF4D00]">{p.label.toLowerCase()}</span>
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {p.benefits.map((b, i) => (
              <div key={i} className="border-l-4 border-[#FF4D00] pl-5 py-1" data-testid={`persona-benefit-${slug}-${i}`}>
                <h3 className="display text-xl leading-tight">{b.h3}</h3>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust protections */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-b-2 border-[#0A0A0B]">
        <div className="mono text-[11px] uppercase tracking-widest text-[#FF4D00] font-semibold">// Les protections Batisur</div>
        <h2 className="display text-3xl lg:text-5xl tracking-tighter mt-3 max-w-3xl leading-[0.95]">
          6 mécanismes de sécurité pour <span className="text-[#FF4D00]">chaque chantier.</span>
        </h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { i: Lock, t: "Séquestre Stripe", d: "Fonds bloqués chez Stripe (agréé bancaire européen) jusqu'à validation." },
            { i: Camera, t: "Preuves horodatées", d: "Photos GPS + SHA-256 opposable devant assureur décennale et justice." },
            { i: BadgeCheck, t: "SIRET vérifié", d: "API Sirene/INSEE en temps réel. Zéro faux artisan, zéro entreprise radiée." },
            { i: Handshake, t: "Équipe médiation", d: "Personnes réelles. Litiges tranchés sous 30 jours max avec preuves opposables." },
            { i: Receipt, t: "Factur-X 2026", d: "Factures PDF/A-3 + XML CII conformes obligatoire 2026. Comptabilité clean." },
            { i: ShieldCheck, t: "Décennale vérifiée", d: "Assurance décennale de chaque artisan contrôlée à l'inscription. Vos travaux couverts 10 ans." },
          ].map(({ i: Icon, t, d }, i) => (
            <div key={i} className="border-2 border-[#0A0A0B] bg-white p-5 shadow-[3px_3px_0_#0A0A0B]">
              <div className="w-10 h-10 border-2 border-[#0A0A0B] bg-[#FF4D00] flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="display text-lg leading-tight">{t}</div>
              <p className="text-sm text-zinc-700 mt-2">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#FF4D00] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-5xl mx-auto px-6 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="mono text-[11px] uppercase tracking-widest text-white/90 font-semibold">// Prêt à démarrer</div>
            <h2 className="display text-3xl lg:text-4xl tracking-tighter mt-2 leading-tight">
              {p.hero.h1.length > 50 ? p.hero.h1.slice(0, 60) + "…" : p.hero.h1}
            </h2>
            <p className="mt-2 text-white/90 max-w-2xl">
              Inscription 60 secondes. Aucune CB requise. Vous payez uniquement lorsque vous êtes satisfait.
            </p>
          </div>
          <Link to={p.ctaLink}>
            <button className="btn-anthracite shadow-[4px_4px_0_#0A0A0B] flex items-center gap-2" data-testid={`persona-cta-final-${slug}`}>
              {p.ctaTitle} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
